import { useMemo, useState, useRef, useEffect, useCallback } from "react";
import { buildGraph, layout } from "../utils/bookGraph";
import { getPlanetStyle } from "../utils/format";

/**
 * 겹치는 책으로 이어지는 우주.
 *
 * 예전 우주 뷰는 이웃 행성만 그렸다. 이웃 기준이 "겹치는 책 / 내 권수 ≥ 30%"
 * 라서 많이 읽은 사람일수록 이웃이 생기지 않았다 — 50권을 담은 사람은
 * 15권이 겹쳐야 한 명이 이어진다.
 *
 * 기준을 낮춰도 채워지지 않는다. 한 권이라도 겹치는 사람 쌍이 253쌍 중
 * 19쌍뿐이라, 사람끼리 잇는 선은 19개가 천장이다.
 *
 * 그래서 겹치는 책을 가운데 노드로 둔다. 같은 자료로 선이 49개가 된다.
 * 기준도 없앤다 — 한 권만 겹쳐도 이어진다.
 */

const W = 1000;
const H = 720;

/* 제목을 짧게. 괄호 안은 서점 표기라 떼고(2026 제71회 … 수상소설집),
   그래도 길면 잘라 …을 붙인다. 자르지 않으면 한 줄이 화면을 가로질러
   가운데 행성 위를 덮는다. */
const SHORT = 12;
const shortTitle = (t) => {
  const bare = String(t ?? "").replace(/[([][^)\]]*[)\]]/g, " ").replace(/\s+/g, " ").trim();
  return bare.length > SHORT ? `${bare.slice(0, SHORT)}…` : bare;
};

/** 라벨 겹침 제거. 중요한 것부터 놓고, 놓을 자리가 막혔으면 비켜 놓는다.
 *  안 걸러내면 빽빽한 자리에서 글자가 서로 위에 쌓여 아무것도 못 읽는다.
 *
 *  k 는 지금 확대 배율이다. 글자를 1/√k 로 줄여 그린다 — 화면에서는
 *  확대할수록 조금씩 커지면서도(4배에서 2배), 그림 안에서 차지하는 자리는
 *  줄어 처음에 밀려났던 제목이 들어온다. "당겨 보면 이름이 보인다" 가
 *  지도의 자연스러운 규칙이다.
 *
 *  1/k 로 줄여도 자리는 더 나지만, 글자가 화면에서 늘 같은 크기라 커진
 *  행성 옆에서 개미만 하게 보였다. */
function placeLabels(nodes, k = 1) {
  const boxes = [];
  /* 글자 기준 길이를 이 그림의 좌표로 옮긴다 */
  const px = (v) => v / Math.sqrt(k);
  const free = (b) => {
    /* 틀 밖으로 나가는 라벨은 놓지 않는다. 바깥쪽 노드의 긴 제목은 절반이
       잘려, 있으나 없으나 읽을 수 없다. */
    if (b.x < 2 || b.x + b.w > W - 2 || b.y < 2 || b.y + b.h > H - 2) return false;
    return !boxes.some((o) => !(b.x + b.w < o.x || o.x + o.w < b.x || b.y + b.h < o.y || o.y + o.h < b.y));
  };
  /* 오른쪽 위는 '⛶ 전체 화면' 버튼이 떠 있는 자리다. 비워두지 않으면 그
     밑에 라벨이 깔려 글자만 반쯤 삐져나온다. 전체 화면에서는 버튼을 그림
     밖으로 빼므로(CSS) 이 자리가 필요 없지만, 한 칸 비는 정도라 그냥 둔다. */
  boxes.push({ x: W - 260, y: 0, w: 260, h: 78 });
  /* 글자끼리만 견주었더니 점 위에 글자가 얹혔다. 노드와, 노드에 딸린
     글씨('3명' · '아직 겹치는 책이 없어요')가 차지한 자리를 먼저 채워둔다. */
  for (const n of nodes) {
    const s = n.kind === "person" ? (n.isMe ? 26 : 21) : 12 + (n.count - 2) * 5;
    const r = s * 1.15;
    boxes.push({ x: n.px - r, y: n.py - r, w: r * 2, h: r * 2 });
    if (n.kind === "book" && n.count >= 3) boxes.push({ x: n.px - 22, y: n.py + s * 1.3 + 10, w: 44, h: 20 });
    if (n.kind === "person" && n.alone) boxes.push({ x: n.px - 80, y: n.py + s + 20, w: 160, h: 20 });
  }

  // 사람은 늘 먼저, 책은 읽은 사람이 많은 것부터
  const ordered = [...nodes].sort(
    (a, b) => (b.kind === "person" ? 1e4 : b.count) - (a.kind === "person" ? 1e4 : a.count)
  );
  const out = new Map();
  for (const n of ordered) {
    const person = n.kind === "person";
    const text = person ? n.user?.name ?? "" : shortTitle(n.title);
    if (!text) continue;
    const s = person ? (n.isMe ? 26 : 21) : 12 + (n.count - 2) * 5;
    /* 자리를 한 곳만 보고 막히면 접었더니, 책 이름이 거의 남지 않아 우주에
       점만 떠 있었다. 위가 막히면 아래·옆으로 비켜 보고, 그래도 안 들어가면
       한 호수 작게 해서 다시 본다. */
    let put = null;
    for (const fsScreen of person ? [15, 13] : [16, 13]) {
      const fs = px(fsScreen);
      /* 한글은 글자 폭이 글자 크기와 거의 같다. 0.95 로 잡으면 실제보다 좁게
         재어 "안 겹친다" 고 판정하고는 그려 놓고 겹쳤다. 여유까지 얹는다. */
      const w = text.length * fs * 1.06 + px(14);
      const h = fs + px(10);
      /* 노드와의 간격은 줄이지 않는다. 글자와 함께 줄였더니 큰 노드의
         자리(반지름의 1.15배)를 파고들어, 배율이 높을수록 큰 책의 제목이
         오히려 밀려났다(2.7배에서 13개 → 9개). */
      const g = s + 12;
      const mid = n.py + fs * 0.35;
      const spots = person
        ? [[n.px, n.py + s + fs + px(8), "middle"], [n.px, n.py - g, "middle"], [n.px + g, mid, "start"], [n.px - g, mid, "end"]]
        : [[n.px, n.py - g, "middle"], [n.px, n.py + s + fs + px(12), "middle"], [n.px + g, mid, "start"], [n.px - g, mid, "end"]];
      for (const [x, y, anchor] of spots) {
        const bx = anchor === "start" ? x : anchor === "end" ? x - w : x - w / 2;
        const box = { x: bx, y: y - h, w, h };
        if (free(box)) { put = { text, fs, x, y, anchor }; boxes.push(box); break; }
      }
      if (put) break;
    }
    /* 사람 이름은 접지 않는다. 누구의 행성인지가 이 그림의 전부라, 이름이
       빠지면 색깔 구슬만 떠 있는 그림이 된다. 네 자리가 다 막혔으면 겹치는
       것을 무릅쓰고 아래에 붙인다. */
    if (!put && person) {
      const fs = px(13);
      put = { text, fs, x: n.px, y: n.py + s + fs + px(8), anchor: "middle" };
      boxes.push({ x: n.px - (text.length * fs) / 2, y: put.y - fs - px(10), w: text.length * fs, h: fs + px(10) });
    }
    if (put) out.set(n.id, put);
  }
  return out;
}

const BookUniverse = ({ users, allBooks, myId, onUserClick, onBookClick }) => {
  /* 폰에서는 전체를 그리면 노드가 서른 개 넘게 겹쳐 글씨를 읽을 수 없다.
     내 주변만 먼저 보여주고, 전체는 눌러서 본다. */
  const [scope, setScope] = useState("near");

  const titles = useMemo(() => {
    const m = new Map();
    for (const b of allBooks || []) m.set(b.id, b.title);
    return m;
  }, [allBooks]);

  const { laid, edges, at } = useMemo(() => {
    let pool = users || [];
    if (scope === "near") {
      const mine = new Set((pool.find((u) => u.id === myId)?.bookIds) || []);
      pool = pool
        .filter((u) => u.id === myId || (u.bookIds || []).some((b) => mine.has(b)))
        // 나와 겹치지 않는 책까지 끌고 오면 '내 주변' 이 전체와 다를 바 없어진다
        .map((u) => (u.id === myId ? u : { ...u, bookIds: (u.bookIds || []).filter((b) => mine.has(b)) }));
    }
    const g = buildGraph(pool, myId);
    const nodes = layout(g.nodes, g.edges, { width: W, height: H, iters: 400 }).map((n) =>
      n.kind === "book"
        ? { ...n, title: titles.get(n.bookId) ?? "" }
        : { ...n, isMe: n.user?.id === myId }
    );
    return { laid: nodes, edges: g.edges, at: new Map(nodes.map((n) => [n.id, n])) };
  }, [users, myId, scope, titles]);

  /* 확대·이동. viewBox 를 건드리지 않고 안쪽 <g> 만 옮긴다 —
     viewBox 를 바꾸면 화면 폭에 맞춰 늘어나는 성질까지 함께 흔들린다. */
  const [tf, setTf] = useState({ k: 1, x: 0, y: 0 });

  /* 배율이 조금 흔들릴 때마다 라벨을 다시 놓으면 글자가 깜빡인다.
     0.25 칸으로 끊어 그 칸을 넘을 때만 다시 놓는다. */
  const kStep = Math.round(Math.min(tf.k, 4) * 4) / 4;
  const labels = useMemo(() => placeLabels(laid, kStep), [laid, kStep]);
  const [full, setFull] = useState(false);
  const svgRef = useRef(null);
  const touch = useRef(null);
  /* 끌고 나서 손을 떼면 브라우저가 클릭으로도 쳐준다. 그대로 두면 그림을
     옮기려다 노드 위에서 떼는 순간 그 책 화면으로 넘어간다 — 전체 화면에서
     볼 수가 없다. 움직인 거리를 재어 '끈 것' 이면 이어지는 클릭을 삼킨다. */
  const dragged = useRef(false);

  const reset = useCallback(() => setTf({ k: 1, x: 0, y: 0 }), []);

  /* 확대는 전체 화면에서만 받는다.
     본문 안에서 핀치를 받으려면 touch-action 을 배율에 따라 바꿔야 하는데,
     확대하는 도중에 pan-y 에서 none 으로 뒤집히는 순간 브라우저가 진행 중인
     손가락 동작을 취소한다. 그래서 끊겼다.
     전체 화면에는 뒤에서 굴릴 본문이 없으니 touch-action 을 계속 none 으로
     두면 되고, 값이 바뀌지 않으므로 취소될 일도 없다. */
  /* 1배가 이미 전체가 들어오는 크기라 더 줄일 이유는 없다. 그래도 1 에서
     딱 막으면 손가락을 오므렸을 때 아무 반응이 없어 고장으로 느껴진다.
     조금 열어두고, 정확히 1 로는 '원래대로' 가 되돌린다. */
  /* 배율을 바꿀 때마다 자리를 다시 잡으므로, 손가락 사이 한가운데가
     화면에서 움직이지 않는다. 이게 없으면 확대할 때 그림이 달아난다. */
  /* 그림이 화면 밖으로 완전히 빠지지 않게 묶는다. 제한이 없으면 한 번
     밀어낸 뒤 '원래대로' 말고는 돌아올 길이 없다. */
  const clamp = (k, x, y) => {
    /* 배율이 1이면 W-W*k 가 0 이라 이동 범위가 0~0 으로 못박혔다. 주석에는
       "1배에서도 끌 수 있게 둔다" 고 적어놓고 실제로는 한 픽셀도 움직이지
       않았다 — 전체 화면에서 손가락으로 끌어도 아무 일이 없던 이유다.
       겹친 라벨을 비켜 보려면 확대하지 않은 채로도 움직여야 하므로,
       한 변의 25% 만큼은 늘 밀 수 있게 둔다. */
    const pad = 0.25;
    const lx = W - W * k, ly = H - H * k;
    const bx = [Math.min(lx, -W * pad), Math.max(0, W * pad)];
    const by = [Math.min(ly, -H * pad), Math.max(0, H * pad)];
    return {
      k,
      x: Math.min(Math.max(x, bx[0]), bx[1]),
      y: Math.min(Math.max(y, by[0]), by[1]),
    };
  };
  const zoomAt = (nk, cx, cy) => setTf((p) => {
    const k = Math.min(6, Math.max(0.55, nk));
    return clamp(k, cx - ((cx - p.x) / p.k) * k, cy - ((cy - p.y) / p.k) * k);
  });

  /** 화면 좌표를 viewBox 좌표로. 폭에 맞춰 줄어든 만큼 되돌려야 한다. */
  const toLocal = (clientX, clientY) => {
    const r = svgRef.current?.getBoundingClientRect();
    if (!r) return [0, 0];
    return [((clientX - r.left) / r.width) * W, ((clientY - r.top) / r.height) * H];
  };

  const onWheel = (e) => {
    if (!full) return;
    e.preventDefault();
    const [cx, cy] = toLocal(e.clientX, e.clientY);
    zoomAt(tf.k * (e.deltaY < 0 ? 1.12 : 1 / 1.12), cx, cy);
  };

  const onTouchStart = (e) => {
    if (!full) return;
    if (e.touches.length === 2) {
      const [a, b] = [e.touches[0], e.touches[1]];
      dragged.current = true;
      touch.current = {
        mode: "pinch",
        d: Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY),
        k: tf.k,
        c: toLocal((a.clientX + b.clientX) / 2, (a.clientY + b.clientY) / 2),
      };
    } else if (e.touches.length === 1) {
      /* 배율이 1이어도 끌 수 있게 둔다. 전체 화면에는 뒤에서 굴릴 본문이
         없으니 손가락을 양보할 이유가 없고, 겹친 라벨을 비켜 보려면
         확대하지 않은 채로도 옮길 수 있어야 한다. */
      const [x, y] = toLocal(e.touches[0].clientX, e.touches[0].clientY);
      touch.current = { mode: "pan", x, y, tx: tf.x, ty: tf.y, sx: e.touches[0].clientX, sy: e.touches[0].clientY };
      dragged.current = false;
    } else {
      touch.current = null;
    }
  };

  const onTouchMove = (e) => {
    if (!full) return;
    const t = touch.current;
    if (!t) return;
    if (t.mode === "pinch" && e.touches.length === 2) {
      e.preventDefault();
      const [a, b] = [e.touches[0], e.touches[1]];
      const d = Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
      zoomAt(t.k * (d / (t.d || 1)), t.c[0], t.c[1]);
    } else if (t.mode === "pan" && e.touches.length === 1) {
      e.preventDefault();
      /* 8px 은 손가락이 가만히 있으려 해도 흔들리는 폭이다. 그 아래는
         탭으로 본다 — 임계값이 없으면 가벼운 탭도 끌기로 먹혀 아무 데도
         못 들어간다. */
      if (Math.hypot(e.touches[0].clientX - t.sx, e.touches[0].clientY - t.sy) > 8) dragged.current = true;
      const [x, y] = toLocal(e.touches[0].clientX, e.touches[0].clientY);
      setTf((p) => clamp(p.k, t.tx + (x - t.x) * p.k, t.ty + (y - t.y) * p.k));
    }
  };
  const onTouchEnd = () => { touch.current = null; };

  /* 마우스로 끌어 옮기기. 손가락은 위에서 받는데 마우스는 받는 곳이 없어,
     데스크톱에서는 휠로 당기기만 되고 옆으로 옮길 수가 없었다.

     누르는 그 자리에서 창 전체에 듣기를 건다 — 상태를 바꾸고 useEffect 로
     걸면 React 가 다시 그린 뒤에야 걸려서, 누르자마자 빠르게 끈 움직임을
     놓친다. 창 전체에 거는 이유는 svg 밖으로 끌고 나가도 이어지고, 밖에서
     손을 떼도 끌던 상태가 남지 않게 하기 위해서다. */
  const [grabbing, setGrabbing] = useState(false);
  const onMouseDown = (e) => {
    if (!full || e.button !== 0) return;
    e.preventDefault();
    const [x0, y0] = toLocal(e.clientX, e.clientY);
    const from = { x: x0, y: y0, tx: tf.x, ty: tf.y, sx: e.clientX, sy: e.clientY };
    dragged.current = false;
    setGrabbing(true);

    const move = (ev) => {
      /* 8px 은 손이 가만히 있으려 해도 흔들리는 폭. 그 아래는 클릭으로 본다 */
      if (Math.hypot(ev.clientX - from.sx, ev.clientY - from.sy) > 8) dragged.current = true;
      const [x, y] = toLocal(ev.clientX, ev.clientY);
      setTf((p) => clamp(p.k, from.tx + (x - from.x) * p.k, from.ty + (y - from.y) * p.k));
    };
    const up = () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", up);
      setGrabbing(false);
    };
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
  };

  /* 끌고 난 직후의 클릭만 삼킨다. 표시는 바로 비워, 다음 탭은 정상으로 든다. */
  const tapped = (run) => () => {
    if (dragged.current) { dragged.current = false; return; }
    run();
  };

  /* 전체 화면에서 Esc 로 빠져나온다. 닫는 길이 ✕ 하나뿐이면
     그 버튼이 그림에 가려질 때 갇힌다. */
  useEffect(() => {
    if (!full) return undefined;
    const onKey = (e) => { if (e.key === "Escape") setFull(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [full]);

  /* 화면 뒤에서 본문이 계속 굴러가면 전체 화면이 뚫린 것처럼 보인다 */
  useEffect(() => {
    if (!full) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [full]);

  if (laid.length === 0) {
    return (
      <div className="bu-empty">
        아직 다른 사람과 겹치는 책이 없어요.<br />
        책을 담으면 같은 책을 읽은 행성과 이어집니다 🪐
      </div>
    );
  }

  /* data-noswipe: 앱 전체에 걸린 '오른쪽으로 쓸면 뒤로가기' 를 이 안에서는 끈다.
     그림을 오른쪽으로 끄는 것이 뒤로가기로 읽혀 홈으로 튀었다. */
  return (
    <div className={full ? "bu-wrap bu-full" : "bu-wrap"} data-noswipe={full ? "" : undefined}>
      <div className="bu-scope">
        <button className={scope === "near" ? "on" : ""} onClick={() => setScope("near")}>내 주변</button>
        <button className={scope === "all" ? "on" : ""} onClick={() => setScope("all")}>전체</button>
      </div>

      <div className="bu-frame">
        <div className="bu-tools">
          {full && (
            <>
              {/* 손가락이 뜻대로 안 될 때 누를 것이 있어야 한다 */}
              <button className="bu-tool bu-zoom" onClick={() => zoomAt(tf.k / 1.4, W / 2, H / 2)} aria-label="축소">－</button>
              <button className="bu-tool bu-zoom" onClick={() => zoomAt(tf.k * 1.4, W / 2, H / 2)} aria-label="확대">＋</button>
            </>
          )}
          {full && Math.abs(tf.k - 1) > 0.01 && <button className="bu-tool" onClick={reset}>원래대로</button>}
          <button className="bu-tool" onClick={() => { setFull((v) => !v); reset(); }}>
            {full ? "✕ 닫기" : "⛶ 전체 화면"}
          </button>
          {/* 끌기·확대는 전체 화면에서만 받는다(아래 onTouchStart 참고).
              적어두지 않으면 여기서 손가락을 대 보고 안 움직인다고 여긴다. */}
        </div>

      <svg
        ref={svgRef}
        className="bu-svg"
        viewBox={`0 0 ${W} ${H}`}
        role="img"
        aria-label="겹치는 책으로 이어진 우주"
        /* 값이 바뀌지 않는다. 손가락 동작 중에 바뀌면 브라우저가 그 동작을
           취소한다. 본문 안에서는 늘 pan-y 라 그림 위에서도 화면이 내려간다. */
        style={{ touchAction: full ? "none" : "pan-y", cursor: full ? (grabbing ? "grabbing" : "grab") : undefined }}
        onWheel={onWheel}
        onMouseDown={onMouseDown}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onDoubleClick={full ? reset : undefined}
      >
        <defs>
          <radialGradient id="buHalo" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#c9a84c" stopOpacity=".5" />
            <stop offset="100%" stopColor="#c9a84c" stopOpacity="0" />
          </radialGradient>
        </defs>

        <g transform={`translate(${tf.x} ${tf.y}) scale(${tf.k})`}>
        {/* 선은 곧게 긋지 않는다. 살짝 휘어야 궤도로 읽힌다. */}
        {edges.map((e, i) => {
          const a = at.get(e.a), b = at.get(e.b);
          if (!a || !b) return null;
          const mx = (a.px + b.px) / 2, my = (a.py + b.py) / 2;
          const dx = b.px - a.px, dy = b.py - a.py;
          return (
            <path key={i} className="bu-edge"
              d={`M${a.px} ${a.py} Q${mx - dy * 0.11} ${my + dx * 0.11} ${b.px} ${b.py}`} />
          );
        })}

        {laid.map((n) => {
          const L = labels.get(n.id);
          if (n.kind === "book") {
            const s = 12 + (n.count - 2) * 5;
            return (
              <g key={n.id} className="bu-node" onClick={tapped(() => onBookClick?.(n.bookId))}>
                <circle cx={n.px} cy={n.py} r={s * 2.4} fill="url(#buHalo)" opacity=".5" />
                {/* 📖 이모지를 쓰다가 점으로 바꿨다. 이모지는 색도 투명도도
                    바꿀 수 없고 기기마다 모양이 달라, 우주에 섞이지 않고
                    스티커처럼 떠 보였다. 읽은 사람이 많을수록 크고 밝다. */}
                <circle className="bu-book-dot" cx={n.px} cy={n.py} r={s * 0.62}
                  opacity={Math.min(0.95, 0.55 + n.count * 0.08)} />
                <circle className="bu-book-ring" cx={n.px} cy={n.py} r={s * 1.05} />
                {/* 맞춤은 style 로 준다 — text-anchor 는 CSS 가 이기므로
                    속성으로 주면 옆에 붙인 라벨이 가운데로 되돌아간다. */}
                {L && (
                  <text className="bu-book-label" x={L.x} y={L.y} fontSize={L.fs}
                    style={{ textAnchor: L.anchor }}>{L.text}</text>
                )}
                {/* 91%가 2명이라 모두 붙이면 같은 말이 22번 반복된다.
                    3명 이상만 — 그때부터가 실제로 눈에 띄는 값이다. */}
                {n.count >= 3 && (
                  <text className="bu-count" x={n.px} y={n.py + s * 1.3 + 26} fontSize="14">{n.count}명</text>
                )}
                {/* 아이콘만으로는 손가락이 닿기 어렵다. 눌리는 자리를 따로 넓게 둔다. */}
                <circle cx={n.px} cy={n.py} r={Math.max(s * 1.7, 24)} fill="transparent" />
              </g>
            );
          }
          const R = n.isMe ? 26 : 21;
          return (
            <g key={n.id} className="bu-node" onClick={tapped(() => onUserClick?.(n.user.id))}>
              <ellipse cx={n.px} cy={n.py} rx={R * 1.75} ry={R * 0.42}
                className={n.isMe ? "bu-ring me" : "bu-ring"}
                transform={`rotate(-18 ${n.px} ${n.py})`} />
              <circle cx={n.px} cy={n.py} r={R * 1.9} className={n.isMe ? "bu-aura me" : "bu-aura"} />
              <foreignObject x={n.px - R} y={n.py - R} width={R * 2} height={R * 2}>
                <div className="bu-orb" style={getPlanetStyle(n.user)}>
                  {n.isMe ? "나" : (n.user?.avatar || "🌿")}
                </div>
              </foreignObject>
              {L && (
                <text className="bu-person-label" x={L.x} y={L.y} fontSize={L.fs}
                  style={{ textAnchor: L.anchor }}>{L.text}</text>
              )}
              {/* 선이 없어 따로 세운 점. 왜 떨어져 있는지 말해준다 */}
              {n.alone && (
                <text className="bu-alone" x={n.px} y={n.py + R + 34}>아직 겹치는 책이 없어요</text>
              )}
            </g>
          );
        })}
        </g>
      </svg>
      </div>
      {/* 끌기·확대는 전체 화면에서만 받는다(onTouchStart 의 !full 참고).
          적어두지 않으면 여기서 손가락을 대 보고 고장난 줄 안다. */}
      {!full && <div className="bu-hint">전체 화면에서 끌어 옮기고 확대할 수 있어요</div>}
      {full && Math.abs(tf.k - 1) > 0.01 && <div className="bu-zoomtag">{tf.k.toFixed(1)}배</div>}
    </div>
  );
};

export default BookUniverse;
